import type {
  GroupRecordStreamSettings,
  PutGroupRequestStreamSettings,
  StreamType,
  TimestampSourceWithoutDefault,
} from '@vlabs/api/artemy/model';

import { stripUndefined } from '@vlabs/monokit/shared/lib/utils';

// По ZEN #9: значения формы без undefined-дефолтов — пустые поля это ''.
// Числовые поля формы: число | ''.

export interface GroupStreamSettingsData {
  type?: StreamType;
  reference?: string;
  timestamp_source?: TimestampSourceWithoutDefault;
  pts?: {
    start_time?: number | '';
  };
  // Угол поворота камеры — data.rotation (как в схеме createStream)
  rotation?: number;
}

export interface GroupStreamSettingsAutorestart {
  restart?: boolean;
  attempt_count?: number | '';
  delay?: number | '';
}

export interface GroupStreamSettingsLocation {
  city?: string;
  area?: string;
  district?: string;
  street?: string;
  house_number?: string;
  geo_position?: {
    latitude?: number;
    longitude?: number;
  };
}

export interface GroupStreamSettings {
  data?: GroupStreamSettingsData;
  location?: GroupStreamSettingsLocation;
  autorestart?: GroupStreamSettingsAutorestart;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return (typeof value === 'object' && value !== null && !Array.isArray(value))
    ? value as Record<string, unknown>
    : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function toFormLocation(raw: Record<string, unknown> | undefined): GroupStreamSettingsLocation | undefined {
  if (!raw) return undefined;
  const geoPosition = asRecord(raw.geo_position);

  const location: GroupStreamSettingsLocation = {
    city: asString(raw.city),
    area: asString(raw.area),
    district: asString(raw.district),
    street: asString(raw.street),
    house_number: asString(raw.house_number),
    // Пустой geo_position ({} или все координаты undefined) в форму не попадает
    geo_position: stripUndefined({
      latitude: asNumber(geoPosition?.latitude),
      longitude: asNumber(geoPosition?.longitude),
    }),
  };

  const hasAny = Object.values(location).some((part) => part !== undefined);
  return hasAny ? location : undefined;
}

/** Безопасное извлечение opaque-настроек группы в типизированную форму.
 *  Пустые числовые поля формы — '' (ZEN #9). */
export function toFormStreamSettings(raw: GroupRecordStreamSettings | undefined): GroupStreamSettings {
  const data = asRecord(raw?.data);
  const autorestart = asRecord(raw?.autorestart);

  return {
    data: {
      type: (typeof data?.type === 'string' ? data.type : undefined) as StreamType | undefined,
      reference: typeof data?.reference === 'string' ? data.reference : undefined,
      timestamp_source: (typeof data?.timestamp_source === 'string'
        ? data.timestamp_source : undefined) as TimestampSourceWithoutDefault | undefined,
      pts: {
        start_time: asNumber(asRecord(data?.pts)?.start_time) ?? '',
      },
      rotation: asNumber(data?.rotation),
    },
    location: toFormLocation(asRecord(raw?.location)),
    autorestart: {
      restart: typeof autorestart?.restart === 'boolean' ? autorestart.restart : undefined,
      attempt_count: asNumber(autorestart?.attempt_count) ?? '',
      delay: asNumber(autorestart?.delay) ?? '',
    },
  };
}

/** Обратная сборка формы в тело PUT: ''-значения числовых полей отбрасываются
 *  (граница формы и апи, ZEN #9), пустые секции не включаются; если секций нет
 *  вовсе — возвращается undefined (бэк не принимает `stream_settings: {}`). */
export function toRequestStreamSettings(
  settings: GroupStreamSettings | undefined,
): PutGroupRequestStreamSettings | undefined {
  const data = { ...settings?.data };
  if (data.pts?.start_time === '') delete data.pts;

  const autorestart = { ...settings?.autorestart };
  if (autorestart.attempt_count === '') delete autorestart.attempt_count;
  if (autorestart.delay === '') delete autorestart.delay;

  const request: PutGroupRequestStreamSettings = {};

  if (Object.values(data).some((value) => value !== undefined)) {
    request.data = data;
  }
  if (settings?.location) {
    const location = { ...settings.location };
    // Бэк при мерже настроек в потоки требует координаты, если ключ geo_position есть
    if (location.geo_position) {
      location.geo_position = stripUndefined(location.geo_position);
    }
    request.location = location;
  }
  if (Object.values(autorestart).some((value) => value !== undefined)) {
    request.autorestart = autorestart;
  }

  if (Object.keys(request).length === 0) return undefined;

  return request;
}
